import useSnackBarContext from '@contexts/snackBarContext'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useCableContext from '@contexts/cableContext'
import { useNavigate } from 'react-router-dom'
import { colors, size } from '@styles/theme'
import styled from '@emotion/styled'
import { ActionButton, ActionButtonContainer, SectionHeader, SectionWrapper } from './AccountElements'
import useYesOrNo from '@hooks/useYesOrNo'
import YesOrNo from '@elements/YesOrNo'

const AccountActionsSection = () => {
  const { state: authState, invalidateTokens, destroyAccount, sendPasswordReset } = useAuthContext()
  const { registerUser } = useModalContext()
  const [isSignOutAllDialogOpen, openSignOutAllDialog, closeSignOutAllDialog] = useYesOrNo('account-signoutall-confirmation')
  const [isPasswordResetDialogOpen, openPasswordResetDialog, closePasswordResetDialog] = useYesOrNo('account-passwordreset-confirmation')
  const [isDestroyAccountDialogOpen, openDestroyAccountDialog, closeDestroyAccountDialog] = useYesOrNo('account-destroyaccount-confirmation')
  const { addSnackBar } = useSnackBarContext()
  const { hideSelf } = useCableContext()
  const navigate = useNavigate()

  const handleSignOutAll = () => invalidateTokens()
    .then(() => navigate('/landing'))
    .finally(() => window.location.reload())
    
  const handleDestroyAccount = () =>  {
    hideSelf(false)
    destroyAccount()
      .then(() => navigate('/landing'))
      .finally(() => window.location.reload())
  }

  const handleSendPasswordReset = () => sendPasswordReset()
    .then(() => addSnackBar({ message: 'Reset password email sent', kind: 'success' }))

  // Sign out all handler for YesOrNo
  const handleSignOutAllYesOrNo = (yesOrNo) => {
    if(yesOrNo) handleSignOutAll();
    closeSignOutAllDialog()
  }

  // Password reset handler for YesOrNo
  const handlePasswordResetYesOrNo = (yesOrNo) => {
    if(yesOrNo) handleSendPasswordReset();
    closePasswordResetDialog()
  }

  // DestroyAccount handler for YesOrNo
  const handleDestroyAccountYesOrNo = (yesOrNo) => {
    if(yesOrNo) handleDestroyAccount();
    closeDestroyAccountDialog()
  }

  const handleManageSubscriptionClick = () => {
    if (authState?.user.isAnonymous) {
      // open registration modal
      registerUser.open()
    } else if (!authState?.user?.isSubscribed) {
      // navigate to subscription page
      navigate('/subscription')
    } else {
      // navigate to subscription management section
      navigate('/settings/manage_subscription')
    }
  }

  return (
    <SectionWrapper>
      <YesOrNo
        isOpen={isSignOutAllDialogOpen}
        setClosed={closeSignOutAllDialog}
        handleSelect={handleSignOutAllYesOrNo}
        title='Sign out of all devices'
        body='This resets the token used for authentication nullifying all existing sessions.'
      />
      <YesOrNo
        isOpen={isPasswordResetDialogOpen}
        setClosed={closePasswordResetDialog}
        handleSelect={handlePasswordResetYesOrNo}
        title='Password Reset'
        body="You should receive an email shortly with a link to reset your password"
      />
      <YesOrNo
        isOpen={isDestroyAccountDialogOpen}
        setClosed={closeDestroyAccountDialog}
        handleSelect={handleDestroyAccountYesOrNo}
        title='Delete Your Account?'
        body="WARNING: This action cannot be undone. Please consider carefully."
      />
      <SectionHeader>
        Account Actions
      </SectionHeader>
      <ActionButtonContainer>
        <ActionButton
          onClick={() => handleManageSubscriptionClick()}
          style={{ backgroundColor: colors.statusColors.right_now('lightRgba')(0.3) }}>
          {authState?.user?.isAnonymous ? 'Register Account' : (authState?.user?.isSubscribed ? 'Manage Subscription' : 'New Subscription')}
        </ActionButton>
        {!authState?.user?.isAnonymous &&
          <>
            <ActionButton
              onClick={() => openPasswordResetDialog()}
              style={{ backgroundColor: colors.statusColors.chatting('lightRgba')(0.3) }}>
              Reset Password
            </ActionButton>
            <ActionButton
              onClick={() => openSignOutAllDialog()}
              style={{ backgroundColor: colors.statusColors.browsing('lightRgba')(0.3) }}>
              Sign Out All
            </ActionButton>
          </>
        }
        <ActionButton
          onClick={() => openDestroyAccountDialog()}
          style={{ backgroundColor: colors.statusColors.romance('lightRgba')(0.3) }}>
          Delete Account
        </ActionButton>
      </ActionButtonContainer>
    </SectionWrapper>
  )
}

export default AccountActionsSection
